import { Box, Button, FormControl, Stack, TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { IconGridSelector } from 'src/components';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  CustomViewAttribute,
  CustomViewEntity,
  CustomViewFormConditionField,
  CustomViewFormFields,
  CustomViewFormModel,
  filterValidCustomViewFilters,
} from 'src/graphql/entities/custom-view';
import { CUSTOM_VIEWS_AVAILABLE_ICONS } from 'src/pages/CustomViews/constants';
import { preventEnterSubmit } from 'src/pages/CustomViews/utils';
import { useScreenDimensions } from 'src/utils/system';
import { CustomViewsActionsHeader } from './custom-views-actions-header';
import { CustomViewsColumnsSelector } from './custom-views-columns-selector';
import { CustomViewsQueryBuilder } from './custom-views-query-builder';
import { MobileFilterWizard } from './mobile-filter-wizard';

interface CustomViewFormProps {
  entityName: CustomViewEntity;
  initialValues: CustomViewFormModel | null;
  onSubmit: (data: CustomViewFormModel) => void;
  onCancel: () => void;
  setValues?: (values: CustomViewFormModel) => void;
  attributes: CustomViewAttribute[];
  filterAttributesMap: Map<string, CustomViewFormConditionField>;
  filterAttributesList: CustomViewFormConditionField[];
  loading?: boolean;
  canBePrivate?: boolean;
}

export const CustomViewForm = ({
  filterAttributesMap,
  filterAttributesList,
  initialValues,
  onSubmit,
  onCancel,
  attributes,
  loading,
  canBePrivate,
  setValues,
}: CustomViewFormProps) => {
  const [isQueryBuilderOpen, setIsQueryBuilderOpen] = useState(false);
  const [columnsSelectorAnchorEl, setColumnsSelectorAnchorEl] =
    useState<null | HTMLButtonElement>(null);
  const { isMobile } = useScreenDimensions();

  const defaultValues = useMemo(() => {
    const emptyValue = {
      rules: [],
      combinator: 'and',
      not: false,
    };

    if (initialValues) {
      return {
        ...initialValues,
        conditions: initialValues.conditions?.combinator
          ? initialValues.conditions
          : emptyValue,
      };
    }

    return {
      icon: CUSTOM_VIEWS_AVAILABLE_ICONS[0],
      name: '',
      description: '',
      isPrivate: false,
      conditions: emptyValue,
      columns: attributes?.filter((i) => i.default).map((i, order) => ({ ...i, order })) ?? [],
    };
  }, [initialValues, attributes]);

  const formMethods = useForm<CustomViewFormModel>({
    defaultValues,
  });

  const { control, handleSubmit, setValue } = formMethods;

  const formValues = useWatch({ control });

  const columns = formValues.columns as CustomViewAttribute[];

  useEffect(() => {
    setValues?.(formValues as CustomViewFormModel);
  }, [formValues, setValues]);

  const handleColumnSelect = useCallback(
    (column: CustomViewAttribute) => {
      const updatedColumns = columns?.find(
        (selectedColumn) => selectedColumn.attribute === column.attribute,
      )
        ? columns?.filter((selectedColumn) => selectedColumn.attribute !== column.attribute)
        : [...(columns ?? []), column];

      const preparedColumns = updatedColumns.map((i, order) => ({
        ...i,
        order,
      })) as CustomViewAttribute[];

      setValue(CustomViewFormFields.Columns, preparedColumns);
    },
    [columns, setValue],
  );

  const handleReorder = useCallback(
    (newColumns: CustomViewAttribute[]) => {
      const preparedColumns = newColumns.map((i, order) => ({
        ...i,
        order,
      }));

      setValue(CustomViewFormFields.Columns, preparedColumns);
    },
    [setValue],
  );

  const handleCloseColumnsSelector = useCallback(() => setColumnsSelectorAnchorEl(null), []);

  const toggleQueryBuilder = useCallback(() => {
    const conditions = formValues[CustomViewFormFields.Conditions];

    if (isQueryBuilderOpen && conditions?.rules?.length) {
      const sanitizedRules = filterValidCustomViewFilters(
        conditions?.rules as CustomViewFormModel['conditions']['rules'],
      );

      setValue(CustomViewFormFields.Conditions, {
        ...conditions,
        not: conditions.not!,
        combinator: conditions.combinator!,
        rules: sanitizedRules,
      });
    }

    setIsQueryBuilderOpen((isOpen) => !isOpen);
  }, [formValues, isQueryBuilderOpen, setValue]);

  const mappedConditions = useMemo(
    () =>
      formValues?.conditions?.rules?.map((i) => ({
        ...i,
        field: filterAttributesMap.get(i.field!.value!)! || i.field,
      })),
    [formValues?.conditions?.rules, filterAttributesMap],
  );

  return (
    <FormProvider {...formMethods}>
      <Stack spacing={2} p="1rem">
        <Stack flexDirection="row" width="100%" alignItems="flex-start" gap="1rem">
          <FormControl>
            <Controller
              name={CustomViewFormFields.Icon}
              control={control}
              render={({ field }) => (
                <IconGridSelector
                  sx={{ marginTop: '1rem' }}
                  icons={CUSTOM_VIEWS_AVAILABLE_ICONS}
                  library="lucide"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormControl>
          <Stack width="100%" spacing={2}>
            <FormControl fullWidth>
              <Controller
                control={control}
                name={CustomViewFormFields.Name}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <TextField
                    fullWidth
                    sx={{
                      alignSelf: 'flex-start',
                      width: '100%',
                    }}
                    error={!!fieldState.error}
                    data-testid="custom-view-form-name"
                    label="Name"
                    variant="standard"
                    {...field}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth>
              <Controller
                control={control}
                name={CustomViewFormFields.Description}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    variant="standard"
                    sx={{
                      alignSelf: 'flex-start',
                      width: '100%',
                    }}
                    label="Description"
                    {...field}
                  />
                )}
              />
            </FormControl>
          </Stack>
        </Stack>

        <CustomViewsActionsHeader
          handleFilterChange={(rules) =>
            setValue(CustomViewFormFields.Conditions, {
              ...(formValues?.conditions as CustomViewFormModel['conditions']),
              rules,
            })
          }
          canBePrivate={canBePrivate}
          // @ts-expect-error need to figure out why the types are not matching
          conditions={mappedConditions}
          toggleQueryBuilder={toggleQueryBuilder}
          setColumnsSelectorAnchorEl={setColumnsSelectorAnchorEl}
          deleteCondition={(id: string) =>
            setValue(CustomViewFormFields.Conditions, {
              ...formValues?.conditions,
              // @ts-expect-error need to figure out why the types are not matching
              rules: formValues?.conditions?.rules?.filter((rule) => rule.id !== id) ?? [],
            })
          }
        />
      </Stack>

      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        sx={{
          padding: '1rem',
          backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainer,
        }}
      >
        <Box>
          <Button onClick={onCancel} variant="text" size="medium" sx={{ marginRight: '1rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={loading}
            size="medium"
            data-testid="custom-view-form-save"
          >
            Save View
          </Button>
        </Box>
      </Stack>

      <CustomViewsColumnsSelector
        onSelect={handleColumnSelect}
        selectedColumns={columns}
        anchorEl={columnsSelectorAnchorEl}
        onReorder={handleReorder}
        onClose={handleCloseColumnsSelector}
        attributes={attributes}
      />
      {isMobile ? (
        <MobileFilterWizard
          open={isQueryBuilderOpen}
          onClose={toggleQueryBuilder}
          name={CustomViewFormFields.Conditions}
          filterAttributesList={filterAttributesList}
        />
      ) : (
        <ModalContainer
          open={isQueryBuilderOpen}
          onClose={toggleQueryBuilder}
          title="Set filters"
          onKeyDown={preventEnterSubmit}
          size="large"
          data-testid="set-filters-modal"
          actions={[
            {
              label: 'Done',
              kind: 'primary',
              onClick: toggleQueryBuilder,
              testId: 'set-filters-save',
            },
          ]}
        >
          <CustomViewsQueryBuilder
            filterAttributesList={filterAttributesList}
            name={CustomViewFormFields.Conditions}
          />
        </ModalContainer>
      )}
    </FormProvider>
  );
};
